import React, { useCallback, useEffect, useState } from "react";
import { LayoutDashboard, Users, Car, Map, Calendar, BarChart3, Plug, ArrowRight, Users2, MapPin } from "lucide-react";
import PanelShell from "../../components/PanelShell";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const navItems = [
  { to: "/admin", label: "Panel", icon: LayoutDashboard, end: true },
  { to: "/admin/reservas", label: "Reservas", icon: Calendar },
  { to: "/admin/conductores", label: "Conductores", icon: Car },
  { to: "/admin/rutas", label: "Rutas", icon: Map },
  { to: "/admin/usuarios", label: "Usuarios", icon: Users },
  { to: "/admin/integraciones", label: "Integraciones", icon: Plug },
  { to: "/admin/reportes", label: "Reportes", icon: BarChart3 },
];
const bottomNav = [
  { to: "/admin", label: "Panel", icon: LayoutDashboard, end: true },
  { to: "/admin/reservas", label: "Reservas", icon: Calendar },
  { to: "/admin/conductores", label: "Choferes", icon: Car },
  { to: "/admin/rutas", label: "Rutas", icon: Map },
  { to: "/admin/reportes", label: "Reportes", icon: BarChart3 },
];

const routeLabel = (r) =>
  r.direction === "aeropuerto_ciudad" ? `${r.airport_name} → ${r.city}` : `${r.city} → ${r.airport_name}`;

const AdminRoutes = () => {
  const { user } = useAuth();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [city, setCity] = useState("");

  const load = useCallback(async (filterCity) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/api/admin/rutas", { params: filterCity ? { city: filterCity } : {} });
      setRoutes(data);
    } catch {
      setError("No se pudieron cargar las rutas.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <PanelShell role="Admin" navItems={navItems} bottomNavItems={bottomNav} userName={user?.full_name} userMeta="Administrador" testId="admin-routes">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display">Catálogo · Solo lectura</p>
            <h1 className="font-display font-bold text-3xl uppercase tracking-tight mt-1">Rutas y tarifas</h1>
            <p className="text-xs text-[#A0A0A5] font-body mt-2">
              Vista de todas las rutas de la flota. La edición la gestiona cada conductor desde su panel.
            </p>
          </div>
          <div className="flex items-center gap-2 bg-[#1C1D20] border border-[#2E2F33] focus-within:border-[#C9A227] rounded-lg px-4 py-2.5">
            <MapPin size={14} className="text-[#C9A227]" />
            <input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load(city)}
              placeholder="Filtrar por ciudad"
              className="bg-transparent outline-none text-sm text-[#F5F5F5] font-body"
              data-testid="admin-routes-city"
            />
            <button
              onClick={() => load(city)}
              className="text-[10px] uppercase tracking-wider font-display text-[#C9A227]"
              data-testid="admin-routes-filter"
            >
              Filtrar
            </button>
          </div>
        </div>

        {error && <p className="mb-4 text-xs text-red-400 font-body" data-testid="admin-routes-error">{error}</p>}

        <div className="bg-[#1C1D20] border border-[#2E2F33] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="routes-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-brand text-[#A0A0A5] font-display border-b border-[#2E2F33]">
                  <th className="text-left px-5 py-3 font-normal">Ruta</th>
                  <th className="text-left px-5 py-3 font-normal">Conductor</th>
                  <th className="text-left px-5 py-3 font-normal">Distancia</th>
                  <th className="text-left px-5 py-3 font-normal">Duración</th>
                  <th className="text-left px-5 py-3 font-normal">Modalidades</th>
                </tr>
              </thead>
              <tbody>
                {!loading &&
                  routes.map((r) => (
                    <tr key={r.id} className="border-b border-[#2E2F33] hover:bg-[#0D0E10]/50 transition" data-testid={`route-row-${r.id}`}>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm text-[#F5F5F5] font-body">{routeLabel(r)}</span>
                          <ArrowRight size={12} className="text-[#C9A227] shrink-0 opacity-0" />
                        </div>
                      </td>
                      <td className="px-5 py-4 font-body text-[#A0A0A5]">{r.driver_name}</td>
                      <td className="px-5 py-4 font-body text-[#A0A0A5]">{r.estimated_distance_km} km</td>
                      <td className="px-5 py-4 font-body text-[#A0A0A5]">{r.estimated_duration_minutes} min</td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          {r.private_enabled && (
                            <span className="px-2 py-0.5 border border-[#2E2F33] rounded-full text-[10px] uppercase tracking-wider text-[#A0A0A5] font-display">
                              Privado · S/ {r.private_price}
                            </span>
                          )}
                          {r.shared_enabled && (
                            <span className="px-2 py-0.5 border border-[#C9A227]/40 rounded-full text-[10px] uppercase tracking-wider text-[#C9A227] font-display flex items-center gap-1">
                              <Users2 size={10} /> Compartido · S/ {r.price_per_seat}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          {loading && <p className="px-5 py-8 text-center text-xs text-[#A0A0A5] font-body">Cargando…</p>}
          {!loading && routes.length === 0 && (
            <p className="px-5 py-8 text-center text-xs text-[#A0A0A5] font-body">No hay rutas registradas.</p>
          )}
        </div>
      </div>
    </PanelShell>
  );
};

export default AdminRoutes;
